import { lang } from 'botpress/shared'
import { AuthStrategyConfig } from 'common/typings'
import CryptoJS from 'crypto-js'
import { get } from 'lodash'
import React, { FC, useEffect, useState } from 'react'
import { RouteComponentProps } from 'react-router'
import api from '~/app/api'
import { ExtendedHistory } from '~/app/history'
import BasicAuthentication, { setActiveWorkspace, setChatUserAuth } from '~/auth/basicAuth'
import { secureLocalStorage, secureSessionStorage } from '~/utils/secureStorage'
import packageJson from '../../../../package.json'
import bgImage from '../assets/images/bg.png'
import logo from '../assets/images/xmati.png'
import { AuthMethodPicker } from './AuthMethodPicker'
import { LoginForm } from './LoginForm'
import '../app/Wizard/style.css'

type RouterProps = RouteComponentProps<
  { strategy: string; workspace: string },
  {},
  { registerUrl?: string; from?: string; email?: string; password?: string; loginUrl?: string }
>

type Props = { auth: BasicAuthentication } & RouterProps & ExtendedHistory

interface AuthConfigResponse {
  payload: {
    strategies: AuthStrategyConfig[]
    isFirstUser: boolean
  }
}

const CURRENT_VERSION = packageJson.version
const API_URL = process.env.REACT_APP_API_URL || 'https://www.app.xmati.ai/apis'
const baseUrl = window.location.origin
const ONLY_ADMIN = (baseUrl.includes('app.xmati.ai')) ? true : false

const Login: FC<Props> = props => {
  const maintenanceStatus = JSON.parse(secureSessionStorage.getItem('maintenance') || '{}')
  const [isLoading, setLoading] = useState(true)
  const [strategies, setStrategies] = useState<AuthStrategyConfig[]>()
  const [loginUrl, setLoginUrl] = useState('')
  const [redirectTo, setRedirectTo] = useState<string>()
  const [error, setError] = useState<string | null>()
  useEffect(() => {
    onStrategyChanged()
  }, [props.match.params.strategy, isLoading])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    initialize()
  }, [])

  const initialize = async () => {
    const routeWorkspaceId = props.match.params.workspace
    const { workspaceId, botId, sessionId, signature, error } = props.location.query

    if (routeWorkspaceId || workspaceId) {
      setActiveWorkspace(routeWorkspaceId || workspaceId)
    }

    if (botId && sessionId && signature) {
      setChatUserAuth({ botId, sessionId, signature })
    }

    if (error) {
      setError(error)
    }

    if (props.auth.isAuthenticated()) {
      await props.auth.afterLoginRedirect()
    }

    if (!strategies) {
      await loadAuthConfig()
    }
  }

  const onStrategyChanged = () => {
    selectStrategy(props.match.params.strategy)

    if (strategies && strategies.length === 1) {
      const excludedRouteRegex = /admin123/ // Regex to match the route
      const isAdminRoute = excludedRouteRegex.test(props.location.pathname)

      if (isAdminRoute) {
        updateAdminUrlStrategy(strategies[0].strategyId)
      } else {
        updateUrlStrategy(strategies[0].strategyId)
      }
      selectStrategy(strategies[0].strategyId)
    }

    if (props.location.state) {
      setRedirectTo(props.location.state.from)
    }
  }

  const loadAuthConfig = async () => {
    const { data } = await api.getAnonymous().get<AuthConfigResponse>('/admin/auth/config')

    setStrategies(data.payload.strategies.filter(x => !x.hidden))
    //setFirstUser(data.payload.isFirstUser)
    setLoading(false)
  }

  const updateUrlStrategy = (strategyId: string) => props.history.push({ pathname: `/login/${strategyId}` })
  const updateAdminUrlStrategy = (strategyId: string) => props.history.push({ pathname: `/login/admin123/${strategyId}` })

  const selectStrategy = (id: string) => {
    const strategy = strategies && strategies.find(x => x.strategyId === id)
    if (!strategy) {
      return setLoginUrl('')
    }

    setError(undefined)

    const { strategyType, strategyId, registerUrl } = strategy

    if (strategyType === 'saml' || strategyType === 'oauth2') {
      return (window.location.href = `${api.getApiPath()}/admin/auth/redirect/${strategyType}/${strategyId}`)
    }

    // if (isFirstUser) {
    //   props.history.push({ pathname: '/register', state: { registerUrl } })
    //   //props.history.push({ pathname: '/wizard' })
    // } else {

    // }
    setLoginUrl(strategy.loginUrl!)
  }

  const loginUser = async (email: string, password: string) => {
    try {
      if (maintenanceStatus.status && email !== 'xmatiservice@gmail.com') {
        setError('Maintenance mode is active. Only Admins are allowed to Login.')
        return
      }

      // if (ONLY_ADMIN && email !== 'xmatiservice@gmail.com') {
      //   setError('Only xMati core admins are allowed to Login.')
      //   return
      // }

      // setLoading(true)
      setError(undefined)

      const status = await userLogin(email, password)
      if (!status.success) {
        setError(status.msg)
        return
      }

      let dbData = status.dbData
      if (status.encrypted && status.encryptedData) {
        try {
          const bytes = CryptoJS.AES.decrypt(status.encryptedData, password)
          const decryptedStr = bytes.toString(CryptoJS.enc.Utf8)
          if (!decryptedStr) {
            throw new Error('Decryption resulted in empty string')
          }
          dbData = JSON.parse(decryptedStr)
        } catch (decryptionError) {
          setError('Failed to decrypt authentication payload')
          return
        }
      }

      secureLocalStorage.setItem('jwt', dbData.jwt)
      secureLocalStorage.setItem('aesKey', dbData.aesKey)

      await setLocalData(dbData)
      const adminEmail = process.env.REACT_APP_ADMIN_EMAIL || ''
      const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD || ''
      await props.auth.login({ owner: dbData.email, email: adminEmail, password: adminPassword }, loginUrl, redirectTo)
    } catch (err) {
      if (err.type === 'PasswordExpiredError') {
        props.history.push({ pathname: '/changePassword', state: { email, password, loginUrl } })
      } else {
        setError(get(err, 'response.data.message', err.message))
      }
    } finally {
      // setLoading(false)
    }
  }

  const userLogin = async (email, password) => {
    try {
      const result = await fetch(`${API_URL}/user-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-App-Version': CURRENT_VERSION
        },
        body: JSON.stringify({
          data: {
            email,
            password
          },
          from: 'login'
        }),
      })
      return result.json()
    } catch (error) {
      return { success: false, msg: 'Error while login' }
    }
  }


  if (isLoading || !strategies) {
    return null
  }

  const setLocalData = async (formData) => {
    const updatedFormData = {
      email: formData.email
    }

    secureLocalStorage.setItem('userData', JSON.stringify(updatedFormData))
  }

  return (
    <div className='parent-wizard-container' style={{
      backgroundImage: `url(${bgImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      width: '100%',
      height: '100vh',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Robot Banner Overlay */}
      <div style={{
        position: 'absolute',
        top: '65%',
        left: '65%',
        transform: 'translateY(-50%)',
        width: 'auto',
        height: 'auto',
        maxWidth: '600px',
        maxHeight: '600px',
        pointerEvents: 'none',
        zIndex: 0,
      }}>
        <img
          src={`${process.env.PUBLIC_URL || ''}/banner-robot.png`}
          alt="Robot Banner"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            opacity: 0.7,
          }}
        />
      </div>
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div className='wizard-header-container' style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 20px',
          whiteSpace: 'nowrap'
        }}>
          <img src={logo} alt='logo' className='wizard-header-logo' />
          <h3 style={{
            flex: 1,
            textAlign: 'center',
            color: 'white',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            Login
          </h3>
          <div style={{ width: '45px' }}></div>
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div className='auth-wizard-container' style={{ width: '50%', maxWidth: '500px', minWidth: '350px' }}>
            <div className='auth-wizard-body'>
              <div className='auth-step'>
                <div style={{
                  textAlign: 'center',
                  fontSize: '28px',
                  fontWeight: 'bold',
                  marginBottom: '5px',
                  fontFamily: 'Lato, sans-serif',
                  color: '#333'
                }}>
                  Welcome Back
                </div>
                <div className='stepSubtitle' style={{ textAlign: 'center', }}>Please sign in to your account</div>
                <div className='stepSubtitle' style={{ textAlign: 'center', marginBottom: '20px' }}>{ONLY_ADMIN ? 'Only DEMO accounts can be logged in' : ''}</div>
                {error && (
                  <div style={{
                    marginBottom: '20px',
                    textAlign: 'center',
                    color: '#ff0000',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    display: 'block',
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#ffe6e6',
                    border: '1px solid #ff9999',
                    borderRadius: '4px'
                  }}>
                    {error}
                  </div>
                )}
                {loginUrl ? (
                  <LoginForm onLogin={loginUser} />
                ) : (
                  <AuthMethodPicker strategies={strategies} onStrategySelected={updateUrlStrategy} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
